<script>
  import Select from "svelte-select/Select.svelte";
  import { get } from "svelte/store";
  import Button from "../../Components/Common/Button.svelte";
  import DefaultDoctorFilter from "../../Components/Common/DefaultDoctorFilter.svelte";
  import {
    CardLayout,
    DoctorMultiSearchSelect,
  } from "../../Components/Common/index.svelte";
  import MultiSearchSelect from "../../Components/Common/MultiSearchSelect.svelte";
  import SearchInput from "../../Components/Common/SearchInput.svelte";
  import { apiMethods } from "../../Services/index.svelte";
  import {
    loggedInDoctorDetail,
    prePopulateAccountOptions,
    user,
  } from "../../stores";
  import { cacheAccountPrePopulateSearchOptions } from "../../Utils/utilCache.svelte";
  import { SEARCH } from "../../Utils/utilConstants.svelte";
  import { BillingStatus, DirectUserRoles } from "../../Utils/utilEnums.svelte";
  import {
    getDaysDifferFrom,
    getSearchSurgeonOptions,
    getTodayWithTime,
    getYYYYMMDD,
    showException,
  } from "../../Utils/utilFunctions.svelte";
  import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

  let filters = {
      startDate: getDaysDifferFrom(-30, new Date()),
      endDate: getYYYYMMDD(new Date()),
    },
    reportType,
    selectedHospital,
    isLoadingProviders,
    hospitalOptions = [],
    filteredProviders = [],
    allDoctors = [],
    isGettingReports,
    selectedProviderDetails,
    selectedDoctors = [];

  const ReportTypeOptions = [
    {
      name: "Payments",
      value: "PaymentsSummary",
      key: "type",
    },
    {
      name: "Encounters",
      value: "EncountersSummary",
      key: "type",
    },
  ];

  const init = () => {
    if (showDoctorFilter) loadDoctors();
  };

  const loadDoctors = async () => {
    if (!$prePopulateAccountOptions.length)
      await cacheAccountPrePopulateSearchOptions();

    allDoctors = get(prePopulateAccountOptions);
  };

  const handleFilterSelect = () => {
    // console.log(filters);
  };

  const loadHospitals = async (searchText) => {
    const res = searchText
      ? await apiMethods.getMethod.getHospitals(0, 999999, {
          search: searchText,
        })
      : [];

    return _handleSearchHospitals(res.results?.length > 0 ? res.results : []);
  };

  const _filterProviders = (arr = []) => {
    if (!arr.length) return [];

    return getSearchSurgeonOptions(arr);
  };

  const _handleSearchHospitals = (res = []) => {
    hospitalOptions = [];

    if (!res.length) return [];

    hospitalOptions = [...res];
    return [...res];
  };

  const handleSelectDoctor = () => {
    filters.doctorId = selectedDoctors.join(",");

    handleFilterSelect();
  };

  const handleDoctorSearch = async (text) => {
    if (!text || text.length < 2) {
      return [];
    }

    isLoadingProviders = true;

    filteredProviders = [];

    await apiMethods.getMethod
      .getAllProviders(0, 999999, { name: text })
      .then((res) => {
        if (!res.error) filteredProviders = _filterProviders(res.results);
        else filteredProviders = [];
      });

    isLoadingProviders = false;
  };

  const createReport = async () => {
    if (selectedHospital && selectedHospital.length) {
      filters.hospitalId = selectedHospital.map((h) => h.id);
    } else {
      delete filters.hospitalId;
    }

    if (selectedProviderDetails && selectedProviderDetails.length) {
      filters.surgeonUniqueId = selectedProviderDetails.map((s) => s.uniqueId);
    } else {
      delete filters.surgeonUniqueId;
    }

    if (isUserDoctor) filters.doctorId = $loggedInDoctorDetail.id;

    const params = {};

    for (const [key, value] of Object.entries(filters)) {
      if (filters[key]) {
        if (Array.isArray(filters[key]) && !!filters[key].length)
          params[`filter[${key}]`] = filters[key].join(",");

        if (typeof filters[key] == "string")
          params[`filter[${key}]`] = filters[key];
      }
    }

    isGettingReports = true;

    if (reportType === "PaymentsSummary") {
      await apiMethods.getMethod
        .getPaymentReports(params)
        .then((res) =>
          handleGeneratedReport(
            res,
            `Payments Report - ${filters.startDate} to ${filters.endDate}`,
          ),
        )
        .catch((err) => showException(err));
    } else {
      await apiMethods.getMethod
        .getEncounterReports(params)
        .then((res) =>
          handleGeneratedReport(
            res,
            `Encounters Report - ${filters.startDate} to ${filters.endDate}`,
          ),
        )
        .catch((err) => showException(err));
    }

    isGettingReports = false;
  };

  const handleGeneratedReport = async (res, name = "file") => {
    res = await res.text();

    const blob = new Blob([res], { type: "text/csv" });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");

    a.setAttribute("href", url);

    a.setAttribute("download", `${name}.csv`);

    a.click();
  };

  const getSubmitDisabledTooltip = (reportType, filters) => {
    if (isGettingReports) return "Generating Report";
    let msg = [];
    if (!reportType) msg.push("Report Type");
    if (!filters.startDate) msg.push("Start Date");
    if (!filters.endDate) msg.push("End Date");

    return msg.join(", ") + " Required";
  };

  $: showDoctorFilter = $user.role >= DirectUserRoles.Support;

  $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

  $: isSubmitDisabled =
    !reportType || !filters.startDate || !filters.endDate || isGettingReports;

  $: disabledTooltip = getSubmitDisabledTooltip(reportType, filters);
</script>

<div class="flex flex-col gap-y-4" use:init>
  <div class="flex flex-row w-full flex-wrap gap-x-4">
    {#if showDoctorFilter}
      <DefaultDoctorFilter
        bind:filter={filters}
        titleClass="text-secondary text-[18px]"
        filterPropertyName={"doctorId"}
        onChangeFunc={() => {
          selectedDoctors = filters["doctorId"].split(",");
          handleFilterSelect();
        }}
      />

      <DoctorMultiSearchSelect
        bind:searchResult={selectedDoctors}
        onChangeFunc={handleSelectDoctor}
        inputClass="w-48"
        zIndex="100"
        titleClass="text-secondary text-[18px]"
      />
    {/if}

    {#if !$user.settings?.disableDateRangeInput}
      <SearchDateRangeInput
        bind:startDate={filters.startDate}
        bind:endDate={filters.endDate}
        title="Date Range"
        inputClass="borderB"
        on:change={handleFilterSelect}
        showScrollYearOptions
        maxYear={new Date(`${getTodayWithTime()}:00`)}
      />
    {:else}
      <SearchInput
        bind:value={filters.startDate}
        title="Start Date"
        type="date"
        titleClass="text-secondary text-[18px]"
        isLong
        minDate={getDaysDifferFrom(-365, new Date())}
        inputClass="borderB w-48"
        inputFunc={handleFilterSelect}
        maxDate={filters.endDate ?? undefined}
        showScrollYearOptions
        maxYear={new Date(`${getTodayWithTime()}:00`)}
      />

      <SearchInput
        bind:value={filters.endDate}
        title="End Date"
        type="date"
        titleClass="text-secondary text-[18px]"
        isLong
        inputClass="borderB w-48"
        inputFunc={handleFilterSelect}
        minDate={filters.startDate ?? undefined}
        showScrollYearOptions
        maxYear={new Date(`${getTodayWithTime()}:00`)}
      />
    {/if}

    <MultiSearchSelect
      title="Billing Status"
      titleClass="text-secondary text-[18px]"
      optionsArr={BillingStatus}
      inputID="billingStatusMultiSelector"
      bind:searchResult={filters.billingStatus}
      onChangeFunc={handleFilterSelect}
      inputClass="w-48"
    />
  </div>

  <CardLayout title="General Report" enableCollapse={false}>
    <div class="flex flex-1 gap-x-2 py-8 px-4">
      <div class="flex flex-col flex-1 gap-y-6">
        <div class="flex flex-col gap-y-4">
          <p class="font-bold text-[18px]">Hospital</p>
          <Select
            bind:value={selectedHospital}
            multiple
            id="hospitalSelect"
            class="flex flex-1 max-h-[35px] rounded-full"
            loadOptions={loadHospitals}
            items={hospitalOptions}
            itemId="id"
            label="name"
            noOptionsMessage="No hospital found"
            placeholder="Hospital"
          />
        </div>

        <div class="flex flex-col gap-y-4">
          <p class="font-bold text-[18px]">Surgeon</p>
          <Select
            bind:value={selectedProviderDetails}
            multiple
            loadOptions={handleDoctorSearch}
            items={filteredProviders}
            itemId="uniqueId"
            label="displayName"
            debounceWait={SEARCH.DEBOUNCE_TIME}
            noOptionsMessage="No Surgeons found"
            placeholder="Surgeon"
            loading={isLoadingProviders}
          />
        </div>

        <div class="flex flex-col gap-y-4">
          <p class="font-bold text-[18px]">Report Type</p>
          <div class="btn-group grid grid-cols-2">
            {#each ReportTypeOptions as button}
              <button
                class={button.value === reportType
                  ? "btn btn-secondary"
                  : "btn btn-outline"}
                on:click={() => (reportType = button.value)}
                >{button.name}</button
              >
            {/each}
          </div>
        </div>

        <div class="flex items-center justify-end">
          <div class="flex flex-1 justify-end">
            <Button
              clickFunc={createReport}
              isDisabled={isSubmitDisabled}
              manualClass="w-48"
              {disabledTooltip}
            >
              Create Report
            </Button>
          </div>
        </div>
      </div>
    </div>
  </CardLayout>
</div>
